import React, { useMemo } from 'react'
import { User } from '../Shared/common_defs'
import { useUserW2Status } from '../../queries/user/useUserW2Status'
import CollapsibleSection from '../Shared/CollapsibleSection'
import { Button, Flex, Table } from '@workwhile/ui'
import UserWorkExperience from './UserWorkExperience'
import { I9DocumentUpload } from './I9DocumentUpload/I9DocumentUpload'
import { defs } from '../Shared/defs'
import { keyValueColumns } from './keyValueColumns'
import { CredentialUpload } from './CredentialUpload'
import {
  UserWorkProfileWorkflowsProvider,
  useUserWorkProfileWorkflowsContextValue,
} from './context/UserWorkProfileWorkflowsProvider'
import { AddWorkerCredentialFlow } from './workflows/AddWorkerCredentialFlow'

interface Props {
  user: User
  refreshData?: () => void
}

const UserWorkProfileContent = ({ user, refreshData }: Props) => {
  const { data: w2Status } = useUserW2Status(user.id)
  const { addWorkerCredentialFlow } = useUserWorkProfileWorkflowsContextValue()

  const data = useMemo(
    () =>
      [
        {
          key: 'has_ssn',
          value: user.hasSsn ? 'true' : 'false',
        },
        {
          key: 'Experienced',
          value: user.tierExperienced ? 'true' : 'false',
        },
        {
          key: 'Rating',
          value: user.rating ? user.rating.toFixed(2) : 0,
        },
        {
          key: '# active strikes',
          value: user.strikes ? user.strikes?.length.toString() : 0,
        },
        {
          key: 'num_finished_shifts',
          value: user.userStats?.shiftCount || '',
        },
        {
          key: 'has_license',
          value: user.workProfile?.hasLicense ? 'true' : 'false',
        },
        {
          key: 'has_car',
          value: user.workProfile?.hasCar ? 'true' : 'false',
        },
        {
          key: 'currently_employed',
          value: user.workProfile?.currentlyEmployed ? 'true' : 'false',
        },
        {
          key: 'num_hours_desired',
          value: user.workProfile?.numHoursDesired || '',
        },
        {
          key: 'wants_full_time',
          value: user.workProfile?.wantsFullTime ? 'true' : 'false',
        },
        {
          key: 'telehealth_benefit_status',
          value: user.workProfile?.telehealthBenefitStatus,
        },
        {
          key: 'current elite_status',
          value: user.workProfile?.isElite ? 'true' : 'false',
        },
        {
          key: 'W2 Status',
          value: w2Status?.w2Status,
        },
        {
          key: 'I-9 Status',
          value: w2Status?.i9Status,
        },
        {
          key: 'I-9 Inquiry Status',
          value: w2Status?.i9InquiryStatus,
          link: w2Status?.i9InquiryId
            ? `https://app.withpersona.com/dashboard/inquiries/${w2Status.i9InquiryId}`
            : undefined,
        },
        Array.isArray(user.w2States) &&
          user.w2States.length && {
            key: 'w2 states',
            value: user.w2States.join(', ') || 'None',
          },
        {
          key: 'Everee Profile',
          value: user.w2EvereeId ? 'View Profile' : 'Not Available',
          ...(user.w2EvereeId && {
            link: `https://app.everee.com/workers/details/${user.w2EvereeId}?tab=PERSONAL`,
          }),
        },
        {
          key: 'Dayforce ID',
          value: user.dayforce?.userId || 'N/A',
        },
        {
          key: 'Dayforce Worker Status',
          value: user.dayforce?.workerStatus || 'N/A',
        },
      ].filter(Boolean),
    [user, w2Status]
  )

  /**
   * Group credentials by workerRequirement.category
   */
  const credentialsData = React.useMemo(() => {
    return user.credentials
      ? user.credentials
          .filter((credential) => credential && credential.workerRequirement)
          .sort((a, b) => {
            const nameA = a.workerRequirement?.name || ''
            const nameB = b.workerRequirement?.name || ''
            return nameA < nameB ? -1 : nameA > nameB ? 1 : 0
          })
          .reduce(
            (acc, credential) => {
              const type = credential.workerRequirement?.category || 'Other'
              if (!acc[type]) {
                acc[type] = []
              }
              acc[type].push({
                key: credential.workerRequirement?.name ?? '',
                value: credential.workerRequirement?.documentNeeded
                  ? 'Click here'
                  : 'Experience',
                ...(credential.workerRequirement?.documentNeeded && {
                  link: `https://console.cloud.google.com/storage/browser/ww-credentials/${credential.id}`,
                }),
              })
              return acc
            },
            {} as Record<string, any[]>
          )
      : [] // return an empty array if user.credentials is null
  }, [user])

  return (
    <CollapsibleSection title="Work Profile" defaultOpen>
      <Table
        variant="borderless"
        data={data as any}
        columns={keyValueColumns}
      />
      <CollapsibleSection title="I-9 Document Upload">
        <I9DocumentUpload user={user as User & { id: string }} />
      </CollapsibleSection>
      <CollapsibleSection title="Credentials">
        {refreshData ? (
          <CredentialUpload user={user} refreshData={refreshData} />
        ) : null}
        {Object.entries(credentialsData).map(([group, credentials]) => {
          return (
            <>
              <CollapsibleSection
                key={group}
                title={group}
                contentStyles={{ margin: 0, padding: defs.paddingS }}
              >
                <Table
                  variant="borderless"
                  data={credentials}
                  columns={keyValueColumns}
                />
                {group === 'Certificates & Experience' && (
                  <Flex mt={2} ml={2}>
                    <Button
                      variant="secondary"
                      kind="small"
                      onClick={addWorkerCredentialFlow.open}
                    >
                      Add Certificate or Experience
                    </Button>
                  </Flex>
                )}
              </CollapsibleSection>
            </>
          )
        })}
      </CollapsibleSection>
      <CollapsibleSection title="Work Experience">
        {({ isOpen }) => <UserWorkExperience user={user} isOpen={isOpen} />}
      </CollapsibleSection>
      <AddWorkerCredentialFlow
        userId={Number(user.id)}
        onSuccess={refreshData}
      />
    </CollapsibleSection>
  )
}

const UserWorkProfile = ({ user, refreshData }: Props) => {
  return (
    <UserWorkProfileWorkflowsProvider>
      <UserWorkProfileContent user={user} refreshData={refreshData} />
    </UserWorkProfileWorkflowsProvider>
  )
}

export default UserWorkProfile
